import {useEffect, useMemo, useState} from "react";
import SocketUtils, {useSocketEvents} from "../components/app/squadflix-tv-mode/socket-utils/socketUtils";
import auth from "../utils/authentication";
import {SquadflixVideo} from "../components/app/squadflix-tv-mode/socket-utils/socket-send-events";

export type SquadflixDevice = {
    deviceId: string;
    linkedAt: string;
    location: string;
    name: string;
    type: string;
    uid: string;
}

export enum PlayingStatus {
    PLAYING = 'playing',
    PAUSED = 'paused',
    STOPPED = 'stopped',
    LOADING = 'loading',
    IDLE = 'idle'
}

export type SquadflixTvPlayingStatus = {
    status: PlayingStatus;
    currentTime?: number;
    videoDuration?: number;
    contentId?: string;
    episodeNumber?: number;
    seasonNumber?: number;
    contentType?: 'movie' | 'series' | 'live' | 'other';
    item?: any;
    contentUrl?: string;
    selectedTrackIndex?: number;
    currentSubtitle?: {
        id: number;
        lang: string;
    }
    videoType?: 'streaming' | 'original';
}

export type SquadflixTvControls = {
    pause: () => void;
    resume: () => void;
    stop: () => void;
    forward: (time: number) => void;
    selectVideoType: (type: 'streaming' | 'original') => void;
    selectSubtitle: (subtitleId?: number) => void;
    changeTrack: (trackIndex: number) => void;
}

export type SquadflixTvHook = {
    connectedReceptor?: SquadflixDevice;
    connectTo: (device?: SquadflixDevice) => void;
    availableReceptors: SquadflixDevice[];
    playingStatus: SquadflixTvPlayingStatus;
    video: SquadflixVideo;
    controls: SquadflixTvControls;
}

const UseSquadflixTv = () => {

    const [availableReceptors, setAvailableReceptors] = useState<SquadflixDevice[]>([]);
    const [connected, setConnected] = useState(false);
    const [connectedReceptor, setConnectedReceptor] = useState<SquadflixDevice>();
    const [playingStatus, setPlayingStatus] = useState<SquadflixTvPlayingStatus>({
        status: PlayingStatus.IDLE
    });
    const [video, setVideo] = useState<SquadflixVideo>();

    const events = useMemo(() => ({
        available_receptors: (data) => {
            if (data?.length) {
                setAvailableReceptors(data.filter(x => x.connected).map(x => x.device));
                if (!data.some(x => x.id == connectedReceptor?.deviceId)) {
                    setConnectedReceptor(undefined);
                }
            }
        },
        connected_receptor: (data) => {
            setConnectedReceptor(data?.device);
        },
        player_status_change: (data) => {
            const isPaused = data?.status?.paused;
            let status = PlayingStatus.IDLE;
            if (isPaused != undefined) {
                status = isPaused ? PlayingStatus.PAUSED : PlayingStatus.PLAYING;
            }
            setPlayingStatus({...data?.status, status});
        },
        selected_video_squadflix: (data) => {
            setVideo(data?.video);
            if (!data?.video) {
                setPlayingStatus({status: PlayingStatus.IDLE});
                return;
            }

            setPlayingStatus({
                status: PlayingStatus.STOPPED,
            });
        }
    }), [connectedReceptor?.deviceId]);

    useSocketEvents({connected, events});

    useEffect(() => {

        if (!auth.currentUser?.uid) {
            return;
        }

        SocketUtils.connect({uid: auth.currentUser.uid, deviceId: undefined}).then(() => {
            setConnected(true);
        });

        return () => {
            SocketUtils.disconnect();
        }
    }, []);

    const connectTo = (device?: SquadflixDevice) => {
        if (!connected) {
            return;
        }

        setConnectedReceptor(device);
        SocketUtils.emit("connect_to_receptor", {deviceId: device?.deviceId});
    }

    const pause = () => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("pause_video", {deviceId: connectedReceptor.deviceId});
    }

    const resume = () => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("resume_video", {deviceId: connectedReceptor.deviceId});
    }

    const stop = () => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("stop_video", {deviceId: connectedReceptor.deviceId});
    }

    const forward = (time: number) => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("forward_time", {deviceId: connectedReceptor.deviceId, time});
    }

    const selectSubtitle = (subtitle: any) => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("select_subtitle", {deviceId: connectedReceptor.deviceId, subtitle});
    }

    const changeTrack = (trackIndex: number) => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("change_track", {deviceId: connectedReceptor.deviceId, trackIndex});
    }

    const selectVideoType = (type: 'streaming' | 'original') => {
        if (!connectedReceptor) {
            return;
        }
        SocketUtils.emit("select_video_type", {deviceId: connectedReceptor.deviceId, type});
    }


    return {
        connectedReceptor, connectTo, availableReceptors, playingStatus, video, controls: {
            pause, resume, stop, forward, selectSubtitle, changeTrack, selectVideoType
        }
    } as SquadflixTvHook;
}

export default UseSquadflixTv;
